import {
  getIndexPagination,
  getIndexPaginationSearchParams,
  normalizeIndexPagination,
  INDEX_PAGE_SIZES,
} from '@shared/lib/pagination';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button, Flex } from '@mantine/core';
import { PageContainer } from '@shared/ui';
import { IconFileExport, IconPlus } from '@tabler/icons-react';
import {
  type UserFiltersQuery,
  UserList,
  UserListFilters,
} from '@features/user';
import { useExportUsers, useGetUsersWithPagination } from '@entities/user';
import { PaginateType, type SortQuery } from '@shared/types';
import classes from './user-list-page.module.scss';
import { useDataExport } from '@shared/ui/modal/hooks/useDataExport';
import type { ExportDataFunction } from '@shared/ui/modal/hooks/useDataExport/types';

export const UserListPage = () => {
  const [filter, setFilter] = useState<UserFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });

  const { mutateAsync: exportUsers, isPending } = useExportUsers();

  const { openChangeDataExport } = useDataExport({
    text: 'Экспортируйте всех пользователей или созданных в заданный период  ',
    title: 'Экспорт пользователей',
    type: 'users',
    exportData: exportUsers as unknown as ExportDataFunction,
    isPending,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const { page, pageSize } = getIndexPagination(searchParams);

  useEffect(() => {
    const normalized = normalizeIndexPagination(searchParams);
    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(
      getIndexPaginationSearchParams(searchParams, nextPage, pageSize)
    );
  };
  const setPageSize = (size: number) => {
    setSearchParams(getIndexPaginationSearchParams(searchParams, 1, size));
  };

  const {
    data: usersData,
    isLoading,
    isFetching,
  } = useGetUsersWithPagination({
    filter,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  return (
    <PageContainer
      title='Пользователи'
      actionButton={
        <Flex gap='0.5rem'>
          <Button
            onClick={openChangeDataExport}
            variant='default'
            leftSection={<IconFileExport />}
            size='md'
          >
            Экспорт
          </Button>
          <Button
            component={Link}
            to='/user-list/create'
            leftSection={<IconPlus />}
            className={classes.addUserButton}
            size='md'
          >
            Создать пользователя
          </Button>
        </Flex>
      }
    >
      <UserListFilters setFilter={setFilter} pageSize={pageSize} />

      <UserList
        users={usersData?.data || []}
        page={page}
        setPage={setPage}
        onRecordsPerPageChange={setPageSize}
        recordsPerPageOptions={INDEX_PAGE_SIZES}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalRecords={usersData?.pagination.total}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        currentSort={sort}
        isLoading={isLoading || isFetching}
      />
    </PageContainer>
  );
};
